/**
 * Server-side structured data for the HighIncomeDownPaymentHelp tool page.
 * Lives in a data module (not the page component) so routeMeta can emit it
 * into the server HTML; react-helmet would only add it after hydration.
 */
export const FAQS = [
  {
    id: "too-much-income",
    q: "I make too much for down payment assistance. Do I have any options?",
    a: "Often yes. Income caps are set program by program, not nationally. National programs delivered through approved lenders may have no borrower income limit at all, bank grants tied to designated census tracts are keyed to the property address rather than your paycheck, and high-cost counties can have income limits far above what buyers expect. Separately, physician and professional loans and low-down-payment first mortgages like Conventional 97 and FHA are not income-capped in the same way. Program terms change, so confirm current rules with a participating lender.",
  },
  {
    id: "census-tract",
    q: "What is a census tract grant and how do I know if a home qualifies?",
    a: "Several large banks run special-purpose credit programs that offer grants or lender credits when the property sits inside a designated census tract. Because eligibility follows the address, a high income does not automatically disqualify you. The only reliable way to check is to run the exact street address through the lender's eligibility lookup before you write an offer — neighboring homes can fall in different tracts, and the maps are updated periodically.",
  },
  {
    id: "physician-loan",
    q: "Is a physician loan actually better than getting a grant?",
    a: "It depends on the numbers. A physician or professional loan is an underwriting exception rather than assistance: it may allow very low or zero down payment, waive mortgage insurance, and treat student loan debt more favorably. Skipping mortgage insurance for years can be worth more than a one-time grant, but the rate may be modestly higher. Compare total cost over how long you actually expect to keep the loan, not just the cash needed at closing.",
  },
  {
    id: "first-time-rule",
    q: "Do I have to be a first-time buyer?",
    a: "Frequently not, and even where a first-time rule applies, most programs define it as not having owned a primary residence during the past three years. Prior homeowners regularly requalify. FHA and physician loans generally have no first-time requirement, and many national assistance programs waive it as well.",
  },
  {
    id: "repay",
    q: "Will I have to pay the assistance back?",
    a: "It depends entirely on the structure. Grants generally are not repaid. Forgivable seconds are written off over a set period as long as you stay in the home. Repayable seconds come due on sale, refinance, or payoff. Ask for the note and the program guide in writing, and confirm exactly which events trigger repayment before you accept the funds.",
  },
  {
    id: "stack",
    q: "Can I combine more than one program?",
    a: "Sometimes. A census tract bank grant may stack with a state program or a low-down-payment first mortgage, but each program sets its own rules about layering with other assistance. Your lender has to confirm the combination is permitted for the specific loan you are using.",
  },
];

export const PAGE_URL = "https://www.homeapproach.com/tools/high-income-down-payment-help";

  /** Single source of truth for the visible "Last reviewed" label and schema dateModified. Keep both in sync. */
export const LAST_REVIEWED = "August 2026";
export const LAST_REVIEWED_ISO = "2026-08";

export const HIGH_INCOME_DOWN_PAYMENT_HELP_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Article",
      headline: "Down Payment Help for High-Income Buyers",
      description:
        "Assistance options for buyers who earn too much for standard down payment assistance income caps: no-income-limit programs, census tract bank grants, high-limit state HFA programs, physician loans, and low-down-payment alternatives.",
      mainEntityOfPage: PAGE_URL,
      url: PAGE_URL,
      dateModified: LAST_REVIEWED_ISO,
      author: { "@type": "Organization", name: "Home Approach", url: "https://www.homeapproach.com" },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: "https://www.homeapproach.com",
        logo: { "@type": "ImageObject", url: "https://www.homeapproach.com/og-image.png" },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://www.homeapproach.com/" },
        { "@type": "ListItem", position: 2, name: "Tools", item: "https://www.homeapproach.com/tools" },
        {
          "@type": "ListItem",
          position: 3,
          name: "Down Payment Help for High-Income Buyers",
          item: PAGE_URL,
        },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};
